import React, { useState } from 'react';
import { AbsoluteFill, Img, useCurrentFrame, useVideoConfig } from 'remotion';
import type { VideoSpec } from './types';
import { FONTS, alpha, headingFamily, headingIsSerif, headingWeight, isDark } from './theme';
import { prog } from './anim';
import { OVERLAP } from './timeline';

export interface SceneCtx {
    spec: VideoSpec;
    frames: number;
}

/** Layout helpers shared by every scene: unit size + orientation. */
export function useLayout() {
    const { width, height } = useVideoConfig();
    const portrait = height > width;
    // Tall canvases have room to spare: type a little larger there.
    const u = (Math.min(width, height) / 1080) * (portrait ? 1.15 : 1);
    return { width, height, u, portrait, square: height === width };
}

/** Scene wrapper: background, cross-fade in/out, gentle zoom. */
export const SceneFrame: React.FC<{ ctx: SceneCtx; bg?: string; children: React.ReactNode; first?: boolean }> = ({ ctx, bg, children, first }) => {
    const frame = useCurrentFrame();
    const { u } = useLayout();
    const t = ctx.spec.theme;
    const background = bg ?? t.background;
    const fadeIn = first ? 1 : prog(frame, 0, OVERLAP);
    const fadeOut = 1 - prog(frame, ctx.frames - OVERLAP, OVERLAP);
    const zoom = 1 + 0.025 * prog(frame, 0, ctx.frames);
    const dots = isDark(background) ? 'rgba(255,255,255,0.07)' : alpha(t.ink, 0.08);
    return (
        <AbsoluteFill style={{ background, opacity: Math.min(fadeIn, fadeOut), fontFamily: FONTS.sans, color: isDark(background) ? '#FFFFFF' : t.ink }}>
            <AbsoluteFill style={{ backgroundImage: `radial-gradient(circle, ${dots} ${1.6 * u}px, transparent ${2.2 * u}px)`, backgroundSize: `${28 * u}px ${28 * u}px` }} />
            <AbsoluteFill style={{ transform: `scale(${zoom})` }}>{children}</AbsoluteFill>
        </AbsoluteFill>
    );
};

export const Rise: React.FC<{ at: number; children: React.ReactNode; dist?: number; style?: React.CSSProperties }> = ({ at, children, dist = 30, style }) => {
    const frame = useCurrentFrame();
    const { u } = useLayout();
    const p = prog(frame, at, 18);
    return <div style={{ opacity: p, transform: `translateY(${(1 - p) * dist * u}px)`, ...style }}>{children}</div>;
};

export const Eyebrow: React.FC<{ color: string; children: React.ReactNode; size?: number }> = ({ color, children, size = 22 }) => {
    const { u } = useLayout();
    return <div style={{ fontFamily: FONTS.mono, fontSize: size * u, letterSpacing: '0.2em', textTransform: 'uppercase', color }}>{children}</div>;
};

export const Heading: React.FC<{ spec: VideoSpec; size: number; children: React.ReactNode; style?: React.CSSProperties }> = ({ spec, size, children, style }) => {
    const { u } = useLayout();
    const serif = headingIsSerif(spec.theme);
    // Caveat runs small and loose: give it a touch more size, no negative tracking.
    const hand = spec.theme.headingFont === 'caveat';
    return (
        <div style={{ fontFamily: headingFamily(spec.theme), fontWeight: headingWeight(spec.theme), fontSize: size * u * (serif ? 1.12 : hand ? 1.18 : 1), lineHeight: serif || hand ? 1 : 1.05, letterSpacing: hand ? '0' : serif ? '-0.025em' : '-0.035em', ...style }}>
            {children}
        </div>
    );
};

/**
 * <Img> that never fails a render: a URL that is not (or no longer) an image
 * shows `fallback` instead of cancelling the whole MP4.
 */
export const SafeImg: React.FC<{ src: string; style?: React.CSSProperties; fallback?: React.ReactNode }> = ({ src, style, fallback = null }) => {
    const [broken, setBroken] = useState(false);
    if (broken) return <>{fallback}</>;
    return <Img src={src} style={style} maxRetries={1} onError={() => setBroken(true)} />;
};

/** Neutral stand-in for a missing picture, tinted with the theme. */
export const ImagePlaceholder: React.FC<{ color: string }> = ({ color }) => (
    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${alpha(color, 0.22)}, ${alpha(color, 0.06)})` }} />
);

/** Brand mark: logo image when given, otherwise a monogram chip. */
export const Brand: React.FC<{ spec: VideoSpec; size?: number; color?: string }> = ({ spec, size = 40, color }) => {
    const { u } = useLayout();
    const { brand, theme } = spec;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 * u }}>
            {brand.logoUrl ? (
                <SafeImg src={brand.logoUrl} style={{ height: size * 1.3 * u, maxWidth: size * 4 * u, objectFit: 'contain' }} />
            ) : (
                <div style={{ width: size * 1.3 * u, height: size * 1.3 * u, borderRadius: 12 * u, background: theme.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.7 * u }}>
                    {brand.name.trim().charAt(0).toUpperCase() || '•'}
                </div>
            )}
            <span style={{ fontWeight: 800, fontSize: size * u, letterSpacing: '-0.02em', color }}>{brand.name}</span>
        </div>
    );
};

export const Check: React.FC<{ size: number; bg: string; fg: string }> = ({ size, bg, fg }) => (
    <span style={{ width: size, height: size, borderRadius: 999, background: bg, color: fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
    </span>
);

/** Corner badge on every scene when brand.showLogo is on: the logo, or a monogram. */
export const LogoBadge: React.FC<{ spec: VideoSpec }> = ({ spec }) => {
    const { u } = useLayout();
    const { brand, theme } = spec;
    const d = 64 * u;
    return (
        <AbsoluteFill style={{ pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: 48 * u, right: 48 * u, height: d, minWidth: d, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {brand.logoUrl ? (
                    <SafeImg src={brand.logoUrl} style={{ height: d, maxWidth: d * 3, objectFit: 'contain' }} />
                ) : (
                    <div style={{ width: d, height: d, borderRadius: 999, background: theme.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans, fontWeight: 800, fontSize: 24 * u, letterSpacing: '0.02em' }}>
                        {monogram(brand.name)}
                    </div>
                )}
            </div>
        </AbsoluteFill>
    );
};

function monogram(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '•').slice(0, 2)).toUpperCase();
}
